import type { ProblemDetails } from "@fp/admin-api-client";
import { FsmPlatform } from "@fp/admin-api-client";
import {
  getGetFsmConnectionByIdQueryKey,
  getListFsmConnectionsQueryKey,
  useGetFsmConnectionById,
  useListFsmConnections,
  useTestFsmConnection,
} from "@fp/admin-api-client/fsm-connections/fsm-connections.client";
import {
  getGetHousecallProConnectionQueryKey,
  useCreateHousecallProConnection,
  useGetHousecallProConnection,
  useUpdateHousecallProConnection,
} from "@fp/admin-api-client/housecall-pro-connections/housecall-pro-connections.client";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
  PageContentContainer,
} from "@fp/ui/components/base/page-layout/page-layout";
import {
  Tooltip,
  TooltipTrigger,
} from "@fp/ui/components/base/tooltip/tooltip";
import { useClipboard } from "@fp/ui/hooks/use-clipboard";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Copy01,
  Eye,
  EyeOff,
  Zap,
} from "@untitledui/icons";
import type { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { toast } from "@/lib/toast";
import { CompletedWorkCard, type CompletedWorkCardCopy } from "./-components/completed-work-card";
import { AutoApprovalSetting } from "./-components/auto-approval-setting";
import { ConnectionCardHeader } from "./-components/connection-card-header";
import { platformPresentation } from "./-components/platform-presentation";
import type { PayoutTriggersCopy } from "./-components/payout-triggers-section";
import { SetupChecklist, type SetupChecklistCopy } from "./-components/setup-checklist";
import { TechsCard, type TechsCardCopy } from "./-components/techs-card";
import { SyncIssuesCard } from "./-components/sync-issues-card";
import type { SyncSwitchCopy } from "./-components/sync-switch";
import { useRefreshConnectionOnVisit } from "./-components/use-refresh-connection-on-visit";

export const Route = createFileRoute("/integrations/housecall-pro")({
  component: RouteComponent,
});

function ConnectForm() {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");

  const createMutation = useCreateHousecallProConnection({
    mutation: {
      onSuccess: () => {
        toast.success("Housecall Pro connected");
        queryClient.invalidateQueries({
          queryKey: getListFsmConnectionsQueryKey(),
        });
      },
      onError: (error: AxiosError<ProblemDetails | undefined>) => {
        const message =
          (error.response?.data as ProblemDetails | undefined)?.title ??
          "Failed to connect";
        toast.error(message);
      },
    },
  });

  const handleConnect = () => {
    if (!apiKey.trim()) {
      toast.error("Please enter your API key");
      return;
    }
    createMutation.mutate({
      data: { apiKey: apiKey.trim() },
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">
        Connect Housecall Pro
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        Enter your Housecall Pro API key to start syncing completed jobs. You
        can find your API key in your Housecall Pro account settings.
      </p>
      <div className="space-y-4">
        <div>
          <label
            htmlFor="hcp-api-key"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            API Key
          </label>
          <input
            id="hcp-api-key"
            type="password"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="Enter your Housecall Pro API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={createMutation.isPending}
          />
        </div>
        <Button
          color="primary"
          onClick={handleConnect}
          isLoading={createMutation.isPending}
        >
          Connect
        </Button>
      </div>
    </div>
  );
}

function ConnectionCard({ connectionId }: { connectionId: string }) {
  const queryClient = useQueryClient();
  const { copy } = useClipboard();
  const [isApiKeyMasked, setIsApiKeyMasked] = useState(true);
  const [apiKeyValue, setApiKeyValue] = useState("");
  const { data: connectionResponse } = useGetFsmConnectionById(connectionId);
  const connection = connectionResponse?.data;

  const { data: revealResponse } = useGetHousecallProConnection(connectionId, {
    query: {
      queryKey: getGetHousecallProConnectionQueryKey(connectionId),
      staleTime: Number.POSITIVE_INFINITY,
    },
  });
  const apiKey = revealResponse?.data?.apiKey ?? "";

  useEffect(() => {
    if (apiKey) {
      setApiKeyValue(apiKey);
    }
  }, [apiKey]);

  const testMutation = useTestFsmConnection({
    mutation: {
      onSuccess: (response) => {
        if (response.data.isSuccess) {
          toast.success("Connection verified");
        } else {
          toast.error(
            "Housecall Pro rejected this API key. Check the key and try again.",
          );
        }
      },
      onError: () => {
        toast.error(
          "Something went wrong testing the connection. Please try again.",
        );
      },
    },
  });

  const updateApiKeyMutation = useUpdateHousecallProConnection({
    mutation: {
      onSuccess: () => {
        toast.success("API key saved");
        setIsApiKeyMasked(true);
        queryClient.invalidateQueries({
          queryKey: getGetFsmConnectionByIdQueryKey(connectionId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetHousecallProConnectionQueryKey(connectionId),
        });
      },
      onError: (error: AxiosError<ProblemDetails | undefined>) => {
        toast.error(
          error.response?.status === 400
            ? "Housecall Pro rejected this API key. Check the key and try again."
            : "Something went wrong saving the API key. Please try again.",
        );
      },
    },
  });

  if (!connection) {
    return null;
  }

  const isVerifying = testMutation.isPending || updateApiKeyMutation.isPending;

  const handleTestOrSave = () => {
    const trimmedApiKey = apiKeyValue.trim();
    if (trimmedApiKey === apiKey) {
      testMutation.mutate({ id: connectionId });
    } else {
      updateApiKeyMutation.mutate({
        id: connectionId,
        data: { apiKey: trimmedApiKey },
      });
    }
  };

  const copyApiKey = async () => {
    if (!apiKey) {
      return;
    }
    const { success } = await copy(apiKey);
    if (success) {
      toast.success("API key copied");
    } else {
      toast.error("Failed to copy API key");
    }
  };

  return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <ConnectionCardHeader connectionId={connectionId} platformName="Housecall Pro" syncSwitchCopy={HOUSECALL_PRO_SYNC_SWITCH_COPY} />
        <dt className="text-sm font-medium text-gray-500">API Key</dt>
        <div className="mt-1 flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <input
              type={isApiKeyMasked ? "password" : "text"}
              value={apiKeyValue}
              onChange={(e) => setApiKeyValue(e.target.value)}
              aria-label="API Key"
              className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <Tooltip
              title={isApiKeyMasked ? "Reveal API key" : "Hide API key"}
              placement="top"
            >
              <TooltipTrigger
                onPress={() => setIsApiKeyMasked((masked) => !masked)}
                aria-label={isApiKeyMasked ? "Reveal API key" : "Hide API key"}
                className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              >
                {isApiKeyMasked ? (
                  <Eye className="size-4" />
                ) : (
                  <EyeOff className="size-4" />
                )}
              </TooltipTrigger>
            </Tooltip>
            <Tooltip title="Copy API key" placement="top">
              <TooltipTrigger
                onPress={copyApiKey}
                aria-label="Copy API key"
                className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              >
                <Copy01 className="size-4" />
              </TooltipTrigger>
            </Tooltip>
          </div>
          <Button
            size="sm"
            color="secondary"
            onClick={handleTestOrSave}
            isLoading={isVerifying}
            isDisabled={!apiKeyValue.trim()}
          >
            <span className="flex items-center gap-1">
              <Zap className="size-4" />
              Test
            </span>
          </Button>
        </div>
        <AutoApprovalSetting
          connectionId={connectionId}
          description="When on, payout requests from Housecall Pro jobs are approved as soon as they are created, except one whose amount still needs allocating across its techs. When off, payout requests wait in the approval queue for review."
        />
      </div>
  );
}

const HOUSECALL_PRO_COMPLETED_WORK_COPY: CompletedWorkCardCopy = {
  syncWindowHelp:
    "This sets how far back Preview and Sync now look for completed jobs. Automatic syncing only picks up jobs completed after sync was turned on — use Sync now to reach further back.",
  previewFoundNothing: "Preview found no completed jobs in the sync window.",
  previewFoundNothingNew:
    "Preview found no new jobs — everything in the sync window has already been processed.",
  syncNowTitle: "Sync from Housecall Pro now",
  syncNowMessage: (days) =>
    `${days === 0 ? "This runs a real sync over today's completed jobs." : `This runs a real sync over the last ${days} ${days === 1 ? "day" : "days"} of completed jobs.`}`,
  syncFoundNothing: "Sync found no completed jobs in the sync window.",
  syncFoundNothingNew:
    "Sync found no new jobs — everything in the sync window has already been processed.",
};

const HOUSECALL_PRO_SYNC_SWITCH_COPY: SyncSwitchCopy = {
  turnOnSyncTitle: "Turn on Housecall Pro sync",
  turnOnSyncMessage:
    "Sync imports completed jobs that match your payout triggers.",
};

const HOUSECALL_PRO_PAYOUT_TRIGGERS_COPY: PayoutTriggersCopy = {
  description:
    "Choose which Housecall Pro price book categories trigger payouts. Only jobs in the selected categories create payout requests.",
  loadFailed: "Failed to load payout triggers from Housecall Pro.",
  noneAvailable: "No categories found in your Housecall Pro price book.",
  noneSelected: "Nothing selected — no jobs will trigger payouts.",
};

const HOUSECALL_PRO_TECHS_COPY: TechsCardCopy = {
  description: "The techs list shows every Housecall Pro tech FieldPay knows about and which techs FieldPay can pay. A tech is payable once Pay is on and the tech is linked to a talent member who has finished payout setup or to an active organization on your roster.",
  payDescription:
    "On means FieldPay pays the tech for completed work. Every new Housecall Pro tech starts on.",
  autoInviteTooltip:
    "FieldPay emails an invitation to every Housecall Pro tech with Pay on who has an email and is not a talent member yet.",
  autoInviteDescription:
    "Invite every Housecall Pro tech with Pay on who is not a talent member yet.",
  notPayableReasons: {},
};

const HOUSECALL_PRO_SETUP_CHECKLIST_COPY: SetupChecklistCopy = {
  payableTechLabel: "At least one tech has Pay on",
};

function ConnectedView({ connectionId }: { connectionId: string }) {
  useRefreshConnectionOnVisit(connectionId);

  return (
    <div className="space-y-6">
      <SetupChecklist
        connectionId={connectionId}
        copy={HOUSECALL_PRO_SETUP_CHECKLIST_COPY}
      />
      <ConnectionCard connectionId={connectionId} />
      <TechsCard
        connectionId={connectionId}
        platformName="Housecall Pro"
        copy={HOUSECALL_PRO_TECHS_COPY}
      />
      <CompletedWorkCard
        connectionId={connectionId}
        platformName="Housecall Pro"
        copy={HOUSECALL_PRO_COMPLETED_WORK_COPY}
        payoutTriggersCopy={HOUSECALL_PRO_PAYOUT_TRIGGERS_COPY}
      />
      <SyncIssuesCard connectionId={connectionId} />
    </div>
  );
}

function RouteComponent() {
  const { data: connectionsResponse } = useListFsmConnections();
  const connections = connectionsResponse?.data ?? [];
  const hcpConnection = connections.find(
    (c) => c.platform === FsmPlatform.HousecallPro,
  );

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Housecall Pro"
        parent={{ url: "/integrations", label: "Integrations" }}
        titleIcon={
          <img
            src={platformPresentation[FsmPlatform.HousecallPro].logoUrl}
            alt="Housecall Pro"
            className="size-8 rounded-lg object-contain"
          />
        }
      />
      <PageContentContainer>
        {connectionsResponse === undefined ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : hcpConnection?.isConnected ? (
          <ConnectedView connectionId={hcpConnection.id} />
        ) : (
          <ConnectForm />
        )}
      </PageContentContainer>
    </AdminLayout>
  );
}
